import React, { useState, useEffect, useRef, useMemo, useTransition } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Layout, Badge, Button, Drawer, Menu, Input, Typography,
  Avatar, Space, Divider, Row, Col, Card, Spin, Dropdown, Tooltip, Skeleton
} from 'antd';
import { motion } from 'framer-motion';
import {
  ShoppingCartOutlined, MenuOutlined, SearchOutlined,
  StarOutlined, CloseOutlined, DownOutlined, HomeOutlined
} from '@ant-design/icons';
import { capitalizeFirst } from '../../utils/stringUtils';
import { fetchCategories, fetchCatalogoInfo, formatImageUrl } from '../../services/api';
import './Header.css';
import logoPrincipal from './logoprincipal.png'; // Importar el logo por defecto
import headerBg from './header.jpeg'; // Importar imagen de fondo

const { Header: AntHeader } = Layout;
const { Search } = Input;
const { Title, Text } = Typography;

// Constantes de colores para la nueva paleta
const COLORS = {
  purple: '#9c27b0',
  fuchsia: '#e91e63',
  lightPurple: '#ce93d8',
  lightFuchsia: '#f8bbd0'
};

const MAX_CATEGORIES_VISIBLE = 8;

// Componente memoizado para resultados de búsqueda para optimizar rendimiento
const SearchResultItem = React.memo(({ product, onSelect }) => (
  <div
    onMouseDown={(e) => {
      e.preventDefault();
      onSelect(product);
    }}
    className="search-result-item"
  >
    <img
      src={formatImageUrl(product.image)}
      alt={product.name}
      className="search-result-image"
    />
    <div className="search-result-info">
      <div className="search-result-name">
        {capitalizeFirst(product.name)}
      </div>
      <div className="search-result-price">
        Bs. {product.price.toFixed(2)}
      </div>
    </div>
  </div>
));

const Header = React.memo(({ cart = [], onSearch, products = [] }) => {
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [categories, setCategories] = useState([]);
  const [topCategories, setTopCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [hoveredCategory, setHoveredCategory] = useState(null);
  const [catalogoInfo, setCatalogoInfo] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searching, setSearching] = useState(false);
  const searchRef = useRef(null);
  const [isPending, startTransition] = useTransition();
  const searchTimeoutRef = useRef(null);

  // Índice de búsqueda pre-calculado para máximo rendimiento (5000+ productos)
  const searchableProducts = useMemo(() => {
    // Usar un loop for tradicional es más rápido que map para 5000+ items
    const len = products.length;
    const result = new Array(len);
    for (let i = 0; i < len; i++) {
      const p = products[i];
      result[i] = {
        id: p.id,
        name: p.name,
        image: p.image,
        price: p.price,
        _searchStr: `${p.name} ${p.categoryName || ''}`.toLowerCase()
      };
    }
    return result;
  }, [products]);

  // Controlar efecto de scroll
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 30);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Cerrar resultados de búsqueda cuando se hace clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };

    if (showSearchResults) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showSearchResults]);

  // Calcular cantidad total de items en carrito
  const cartItemCount = cart.reduce((total, item) => total + item.quantity, 0);

  // Manejar búsqueda con técnica de "Time Slicing" para fluidez nivel Alibaba
  const handleSearch = (value) => {
    // 1. Actualización de teclado instantánea
    setSearchTerm(value);

    if (!value.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      setSearching(false);
      return;
    }

    setSearching(true);
    setShowSearchResults(true);

    // 2. Cancelar búsqueda anterior si existe
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // 3. Pequeño delay de 50ms para dejar que el hilo de UI respire (teclado fluido)
    searchTimeoutRef.current = setTimeout(() => {
      startTransition(() => {
        const lowerValue = value.toLowerCase();
        const results = [];
        const data = searchableProducts;
        const dataLen = data.length;

        // Loop ultra-optimizado: se detiene apenas llega a 12 resultados
        for (let i = 0; i < dataLen; i++) {
          if (data[i]._searchStr.indexOf(lowerValue) !== -1) {
            results.push(data[i]);
            if (results.length >= 12) break;
          }
        }

        setSearchResults(results);
        setSearching(false);
      });
    }, 50);
  };

  // Manejar selección de producto de búsqueda
  const handleProductSelect = (product) => {
    navigate(`/product/${product.id}`);
    setShowSearchResults(false);
    setSearchTerm('');
    setSearchExpanded(false);
    if (onSearch) onSearch(product.name);
  };

  // Cargar categorías
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const data = await fetchCategories();
        setCategories(data); // Usar para el drawer
        setTopCategories(data); // Usar para la barra de header no móvil
      } catch (error) {
        console.error('Error cargando categorías:', error);
      } finally {
        setLoading(false);
      }
    };
    loadCategories();
  }, []);

  // Cargar información del catálogo para el logo
  useEffect(() => {
    const loadCatalogoInfo = async () => {
      try {
        const info = await fetchCatalogoInfo();
        setCatalogoInfo(info);
      } catch (error) {
        console.error('Error al cargar información del catálogo:', error);
      }
    };
    loadCatalogoInfo();
  }, []);

  // Actualizar estado de mobile en resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Actualizar activeCategory desde la URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const categoryIdFromUrl = params.get('category');
    setActiveCategory(categoryIdFromUrl);
  }, [location.search]);


  // Animaciones con Framer Motion
  const logoVariants = {
    normal: { scale: 1 },
    hover: { scale: 1.08, rotate: [0, -5, 5, -5, 0] }
  };

  // Simplificamos el icono para que siempre sea una estrella
  const getCategoryIcon = (name) => {
    return <StarOutlined className="star-icon" />;
  };

  const handleCategoryClick = (category) => {
    navigate(`/?category=${category.id}`);
    setDrawerVisible(false); // Asegurarse de que el drawer se cierre
    setActiveCategory(category.id); // Actualizar categoría activa inmediatamente
  };

  const toggleCategoryExpansion = (categoryId) => {
    setExpandedCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  // Función para obtener el logo dinámico
  const getLogoUrl = () => {
    if (catalogoInfo && catalogoInfo.logo_url) {
      return formatImageUrl(catalogoInfo.logo_url);
    }
    return logoPrincipal; // Logo por defecto
  };

  // Drawer mejorado con estilo consistente con header
  const renderDrawerCategories = () => (
    <div className="categories-drawer-section">
      {/* Botón "Inicio/Todos" para drawer con el mismo estilo del header */}
      <motion.div
        className="drawer-category-pill"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <div
          className={`drawer-category-link home-category ${!activeCategory ? 'active' : ''}`}
          onClick={() => {
            navigate('/');
            setDrawerVisible(false);
            setActiveCategory(null);
          }}
        >
          <HomeOutlined className="drawer-category-icon" />
          <span>Inicio</span>
        </div>
      </motion.div>

      {/* Categorías con mismo estilo que el header */}
      {categories.map(category => (
        <div key={category.id}>
          <motion.div
            className="drawer-category-pill"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <div
              className={`drawer-category-link ${activeCategory === category.id ? 'active' : ''}`}
              onClick={() => {
                if (category.subcategories && category.subcategories.length > 0) {
                  toggleCategoryExpansion(category.id);
                } else {
                  handleCategoryClick(category);
                }
              }}
            >
              <StarOutlined className="drawer-category-icon" />
              <span>{capitalizeFirst(category.name)}</span>
              {category.subcategories && category.subcategories.length > 0 && (
                <DownOutlined
                  rotate={expandedCategories.includes(category.id) ? 180 : 0}
                  className="drawer-expand-icon"
                />
              )}
            </div>
          </motion.div>

          {/* Subcategorías con mismo estilo */}
          {expandedCategories.includes(category.id) && category.subcategories && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="drawer-subcategories-list"
            >
              {category.subcategories.map(sub => (
                <motion.div
                  key={sub.id}
                  className="drawer-subcategory-pill"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <div
                    className={`drawer-subcategory-link ${activeCategory === sub.id ? 'active' : ''}`}
                    onClick={() => handleCategoryClick(sub)}
                  >
                    <StarOutlined className="drawer-subcategory-icon" />
                    <span>{capitalizeFirst(sub.name)}</span>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      ))}
    </div>
  );

  // Categorías visibles y ocultas
  const visibleCategories = categories.slice(0, MAX_CATEGORIES_VISIBLE);
  const moreCategories = categories.slice(MAX_CATEGORIES_VISIBLE);

  // Nuevo orden de elementos: Logo, Input, Categorías, Carrito
  return (
    <header
      className={`site-header card-header ${scrolled || location.pathname === '/cart' ? 'scrolled' : ''}`}
      role="banner"
      aria-label="Cabecera principal"
      style={{
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
        marginTop: 0
      }}
    >
      {/* Layout unificado pero con diferentes organizaciones según dispositivo */}
      <div className="header-unified-container">
        {!isMobile ? (
          /* Desktop Layout - Sin cambios */
          <>
            {/* Logo */}
            <div className="logo-container">
              <Link to="/">
                <motion.div
                  variants={{
                    normal: { scale: 1 },
                    hover: { scale: 1.05 }
                  }}
                  initial="normal"
                  whileHover="hover"
                  transition={{ duration: 0.3 }}
                >
                  <img src={getLogoUrl()} alt="Bisutería Channel's" className="main-logo" />
                </motion.div>
              </Link>
            </div>

            {/* Input de búsqueda */}
            <div className="search-container" ref={searchRef}>
              {searchExpanded ? (
                <div style={{ position: 'relative' }}>
                  <Search
                    autoFocus
                    placeholder="Buscar productos..."
                    allowClear
                    enterButton={<SearchOutlined />}
                    onSearch={value => { handleSearch(value); setSearchExpanded(false); }}
                    onChange={(e) => handleSearch(e.target.value)}
                    value={searchTerm}
                    className="header-search-input"
                  />
                  {/* Resultados de búsqueda */}
                  {showSearchResults && (
                    <div className="search-results-dropdown">
                      {searching ? (
                        // Skeletons mientras busca
                        [1, 2, 3].map(i => (
                          <div key={`search-skeleton-${i}`} className="search-result-item">
                            <Skeleton active avatar={{ shape: 'square' }} paragraph={{ rows: 1 }} />
                          </div>
                        ))
                      ) : searchResults.length > 0 ? (
                        searchResults.map(product => (
                          <SearchResultItem
                            key={product.id}
                            product={product}
                            onSelect={handleProductSelect}
                          />
                        ))
                      ) : (
                        <div style={{ padding: '20px', textAlign: 'center' }}>
                          <Text type="secondary">No se encontraron productos</Text>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <Button
                  type="text"
                  icon={<SearchOutlined style={{ fontSize: 18, color: '#fff' }} />}
                  onClick={() => setSearchExpanded(true)}
                  className="search-button-elegant"
                  aria-label="Buscar"
                />
              )}
            </div>

            {/* Categorías con scroll horizontal */}
            <div className="categories-horizontal-container">
              <div className="categories-horizontal-scroll">
                {/* Botón "Inicio/Todos" */}
                <Link
                  to="/"
                  className={`category-pill home-category ${!activeCategory ? 'active' : ''}`}
                  style={!activeCategory ? { color: COLORS.purple } : {}}
                >
                  <HomeOutlined className="home-icon" />
                  <span>Inicio</span>
                </Link>

                {/* Categorías regulares con primera letra en mayúscula */}
                {!loading && categories.map(category => (
                  <Link
                    key={category.id}
                    to={`/?category=${category.id}`}
                    className={`category-pill ${activeCategory === category.id ? 'active' : ''}`}
                    style={activeCategory === category.id ? { color: COLORS.purple } : {}}
                  >
                    <StarOutlined className="category-star" />
                    <span>{capitalizeFirst(category.name)}</span>
                  </Link>
                ))}
              </div>
            </div>

            {/* Carrito */}
            <div className="cart-container">
              <Badge count={cartItemCount} overflowCount={99} style={{ color: '#fff', background: COLORS.fuchsia, fontWeight: 700, fontSize: 15, border: 'none', boxShadow: '0 2px 8px rgba(233, 30, 99, 0.18)' }}>
                <Button
                  className="cart-button"
                  type="text"
                  icon={<ShoppingCartOutlined />}
                  onClick={() => navigate('/cart')}
                  aria-label="Ir al carrito"
                />
              </Badge>
            </div>
          </>
        ) : (
          /* Mobile Layout - Hamburguesa, Logo Centrado, Carrito + Búsqueda */
          <>
            {/* Hamburguesa a la izquierda */}
            <div className="mobile-menu-container">
              <Button
                className="menu-button-modern"
                type="text"
                icon={<MenuOutlined />}
                onClick={() => setDrawerVisible(true)}
                aria-label="Menú"
              />
            </div>

            {/* Logo centrado */}
            <div className="mobile-logo-container">
              <Link to="/">
                <motion.div
                  variants={logoVariants}
                  initial="normal"
                  whileHover="hover"
                  transition={{ duration: 0.5 }}
                >
                  <img src={getLogoUrl()} alt="Bisutería Channel's" className="mobile-logo" />
                </motion.div>
              </Link>
            </div>

            {/* Búsqueda y Carrito a la derecha */}
            <div className="mobile-actions-container">
              <Button
                type="text"
                icon={<SearchOutlined style={{ fontSize: 16, color: '#fff' }} />}
                onClick={() => setSearchExpanded(true)}
                className="mobile-search-button"
                aria-label="Buscar"
              />

              <Badge count={cartItemCount} overflowCount={99} color={COLORS.fuchsia} style={{ color: '#fff', background: COLORS.fuchsia, fontWeight: 700, fontSize: 15, border: 'none', boxShadow: '0 2px 8px rgba(233, 30, 99, 0.18)' }}>
                <Button
                  className="cart-button"
                  type="text"
                  icon={<ShoppingCartOutlined />}
                  onClick={() => navigate('/cart')}
                  aria-label="Ir al carrito"
                />
              </Badge>
            </div>
          </>
        )}
      </div>

      {/* Modal de búsqueda para móvil */}
      {isMobile && searchExpanded && (
        <div className="mobile-search-modal">
          <div className="mobile-search-content" ref={searchRef}>
            <Search
              autoFocus
              placeholder="Buscar productos..."
              allowClear
              enterButton={<SearchOutlined />}
              onSearch={value => { handleSearch(value); setSearchExpanded(false); }}
              onChange={(e) => handleSearch(e.target.value)}
              value={searchTerm}
              className="mobile-search-input"
              size="large"
            />
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={() => setSearchExpanded(false)}
              className="mobile-search-close"
              style={{ background: COLORS.fuchsia }}
            />
            {/* Resultados de búsqueda móvil */}
            {showSearchResults && (
              <div className="search-results-dropdown mobile-search-results">
                {searching ? (
                  [1, 2, 3].map(i => (
                    <div key={`search-skeleton-mob-${i}`} className="search-result-item">
                      <Skeleton active avatar={{ shape: 'square' }} paragraph={{ rows: 1 }} />
                    </div>
                  ))
                ) : searchResults.length > 0 ? (
                  searchResults.map(product => (
                    <SearchResultItem
                      key={product.id}
                      product={product}
                      onSelect={handleProductSelect}
                    />
                  ))
                ) : (
                  <div style={{ padding: '20px', textAlign: 'center' }}>
                    <Text type="secondary">No se encontraron productos</Text>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Mobile Drawer con estilo elegante y profesional mejorado */}
      <Drawer
        title={null}
        placement="left"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        width={290}
        className="mobile-drawer-elegant"
        closable={false} /* Desactivar botón por defecto */
      >
        <div className="drawer-header-elegant">
          {/* Botón de cerrar PERSONALIZADO dentro de la imagen */}
          <button
            className="custom-drawer-close"
            onClick={() => setDrawerVisible(false)}
            aria-label="Cerrar menú"
          >
            <CloseOutlined />
          </button>

          <h3 className="drawer-header-title">Categorías</h3>
        </div>

        <div className="drawer-content-elegant"
          style={{ background: `linear-gradient(90deg, ${COLORS.purple} 0%, ${COLORS.fuchsia} 60%, ${COLORS.lightFuchsia} 100%)` }}
        >
          {renderDrawerCategories()}
        </div>
      </Drawer>
    </header>
  );
});

export default Header;
